/**
 * Freemium gate - Pro license key issuing + validation.
 *
 * Key format:  AS-PRO-XXXX-XXXX-XXXX-CC
 *   - 12 payload characters from a Crockford-style 32 char alphabet
 *     (no I, O, 0, 1 - avoids transcription mistakes)
 *   - 2 checksum characters derived from the payload
 *
 * The checksum makes random keys fail deterministically while staying 100%
 * offline - no server call, which keeps AlgorithmShield fully client-side.
 */

export const LICENSE_PREFIX = 'AS-PRO';
export const FREE_BATCH_LIMIT = 5;
/** Pro unlocks "100+ images per batch" - we allow far more than the promise. */
export const PRO_BATCH_LIMIT = 1000;

/** 32 symbols, visually unambiguous subset of the alphabet + digits. */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const ALPHABET_SIZE = 32;

const KEY_PATTERN = /^AS-PRO-([A-Z2-9]{4})-([A-Z2-9]{4})-([A-Z2-9]{4})-([A-Z2-9]{2})$/;

export interface LicenseResult {
  valid: boolean;
  tier: 'free' | 'pro';
  message: string;
}

/** Normalises user input: trims, upper-cases and removes whitespace. */
export function normalizeLicenseKey(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, '');
}

function charValue(ch: string): number {
  const index = ALPHABET.indexOf(ch);
  return index < 0 ? -1 : index;
}

/** Two-character checksum for a 12 character payload. */
export function computeChecksum(payload: string): string {
  let weighted = 0;
  for (let i = 0; i < payload.length; i++) {
    const value = charValue(payload[i]);
    if (value < 0) return '??';
    weighted = (weighted + value * (i + 7)) % ALPHABET_SIZE;
  }
  let spread = 0;
  for (let i = 0; i < payload.length; i++) {
    const value = charValue(payload[i]);
    spread = (spread + value * (payload.length - i) * 3) % ALPHABET_SIZE;
  }
  return ALPHABET[weighted] + ALPHABET[spread];
}

/** Deterministic PRNG so tests and the "demo key" generator are reproducible. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Issues a valid Pro key for a numeric seed (used for demo keys & tests). */
export function generateLicenseKey(seed: number): string {
  const rand = mulberry32(seed);
  let payload = '';
  for (let i = 0; i < 12; i++) {
    payload += ALPHABET[Math.floor(rand() * ALPHABET_SIZE)];
  }
  const checksum = computeChecksum(payload);
  return `${LICENSE_PREFIX}-${payload.slice(0, 4)}-${payload.slice(4, 8)}-${payload.slice(8, 12)}-${checksum}`;
}

/** Validates a key and reports the resulting tier. */
export function validateLicenseKey(raw: string): LicenseResult {
  const key = raw.trim().toUpperCase().replace(/\s+/g, '');
  if (!key) {
    return { valid: false, tier: 'free', message: 'Enter a license key to activate Pro.' };
  }
  if (!key.startsWith(`${LICENSE_PREFIX}-`)) {
    return { valid: false, tier: 'free', message: 'Invalid key format. Expected AS-PRO-XXXX-XXXX-XXXX-CC.' };
  }

  const match = KEY_PATTERN.exec(key);
  if (!match) {
    return { valid: false, tier: 'free', message: 'Invalid key format. Expected AS-PRO-XXXX-XXXX-XXXX-CC.' };
  }

  const payload = match[1] + match[2] + match[3];
  const expected = computeChecksum(payload);
  if (match[4] !== expected) {
    return { valid: false, tier: 'free', message: 'Key checksum failed - this license key is not genuine.' };
  }

  return { valid: true, tier: 'pro', message: 'Pro activated: unlimited batches and auto 86% fill scaling.' };
}

/** Max images exportable in a single batch for the given tier. */
export function batchLimitFor(isPro: boolean): number {
  return isPro ? PRO_BATCH_LIMIT : FREE_BATCH_LIMIT;
}
